import { CommonModule, DatePipe, NgForOf, NgIf } from "@angular/common";
import { Component, inject, OnInit } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { AuthService } from "../../../core/services/auth.service";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import {
  TuiAlertService,
  TuiAppearance,
  TuiButton,
  TuiDataList,
  TuiDialog,
  TuiDialogService,
  TuiDropdown,
  TuiLink,
  TuiSizeL,
  TuiSizeS,
  TuiTextfield,
} from "@taiga-ui/core";
import {
  TuiBadgedContent,
  TuiBreadcrumbs,
  TuiDataListDropdownManager,
  TuiDataListWrapper,
  TuiFade,
  TuiItemsWithMore,
  TuiPagination,
  TuiTabs,
} from "@taiga-ui/kit";
import { TuiNavigation, TuiSearch } from "@taiga-ui/layout";
import { TuiTable } from "@taiga-ui/addon-table";
import { TuiInputDateRangeModule, TuiInputModule } from "@taiga-ui/legacy";
import { NurseService } from "../../../core/services/nurse.service";
import { ForgotResetPasswordService } from "../../../core/services/forgot-reset-password.service.service";
import { SalaryService } from "../../../core/services/salary.service";
import { ShiftService } from "../../../core/services/shift.service";
import { LeaveService } from "../../../core/services/leave.service";
import { IShift } from "../../../model/interface/shift";
import { TuiDayRange } from "@taiga-ui/cdk";
import { forkJoin } from "rxjs";
import { DisableUnlessPermissionDirective } from "../../../core/directives/disable-unless-permission.directive";
import {
  getEmployeeNumber,
  PERMANENT_CONVERSION_STATUS,
} from "../../../model/interface/nurse";

@Component({
  selector: "app-single-nurse",
  imports: [
    DisableUnlessPermissionDirective,
    FormsModule,
    NgForOf,
    NgIf,
    TuiAppearance,
    TuiDataListWrapper,
    TuiBreadcrumbs,
    TuiDataList,
    TuiDropdown,
    TuiFade,
    TuiTable,
    TuiLink,
    TuiNavigation,
    TuiTabs,
    TuiTextfield,
    TuiItemsWithMore,
    TuiSearch,
    ReactiveFormsModule,
    CommonModule,
    TuiBadgedContent,
    TuiInputModule,
    RouterLink,
    TuiDataListDropdownManager,
    TuiButton,
    TuiDialog,
    TuiPagination,
    TuiInputDateRangeModule,
  ],
  templateUrl: "./single-nurse.component.html",
  styleUrl: "./single-nurse.component.scss",
  providers: [DatePipe],
})
export class SingleNurseComponent {
  protected breadcrumbs: string[] = ["Royal Hospital", "Nurses"];
  nurseId: string = "";
  nurse: any | null = null;
  balance: any = null;
  salaryRecords: any[] = [];
  upcomingShifts: IShift[] = [];
  completedShifts: IShift[] = [];
  isLoading: boolean = false;
  isFiltering: boolean = false;
  private readonly dialogs = inject(TuiDialogService);
  protected size: TuiSizeL | TuiSizeS = "s";
  protected openDropdownsize: TuiSizeL | TuiSizeS = "s";
  protected open = false;

  upcomingShiftPageIndex = 1;
  shiftPageSize = 5;
  upcomingShiftTotal: number = 0;

  completedShiftPageIndex = 1;
  completedShiftTotal: number = 0;

  salaryPageIndex = 1;
  salaryPageSize = 5;
  salaryTotal: number = 0;

  filterStartDate: string = "";
  filterEndDate: string = "";
  dateRange: TuiDayRange | null = null;
  openMap: { [shiftId: string]: boolean } = {};

  loggedInUserCanEdit = false;
  loggedInUserIsHR = false;
  private authService = inject(AuthService);

  constructor(
    private nurseService: NurseService,
    private router: Router,
    private route: ActivatedRoute,
    private resetPasswordService: ForgotResetPasswordService,
    private salaryService: SalaryService,
    private shiftService: ShiftService,
    private leaveService: LeaveService
  ) { }

  ngOnInit() {
    this.nurseId = this.route.snapshot.paramMap.get("id")!;
    if (this.nurseId) {
      this.loadNurseData();
      this.loadUpcomingShifts();
      this.applyAttendanceMonthFilter();
      this.loadLeaveRecords();
      this.loadLeaveBalance();
    }
    this.loadSalaryRecords();
    this.checkUserPermission();
  }

  loadNurseData(): void {
    this.isLoading = true;
    if (this.nurseId) {
      this.nurseService.getOneNurseById(this.nurseId).subscribe((res: any) => {
        console.log(res);
        this.nurse = res;
        this.breadcrumbs = [
          "Royal Hospital",
          "Nurses",
          getEmployeeNumber(this.nurse),
        ];
        this.isLoading = false;
      });
    }
  }

  loadUpcomingShifts(): void {
    const payload = {
      pageIndex: this.upcomingShiftPageIndex,
      pageSize: this.shiftPageSize,
      sortField: "date",
      sortOrder: -1,
      startDate: this.filterStartDate,
      endDate: this.filterEndDate,
    };

    this.shiftService
      .getPagedShift(payload, this.nurseId)
      .subscribe((res: any) => {
        if (res && res.data && res.data.length) {
          const pagedResult = res.data[0];
          this.upcomingShifts = pagedResult.data;
          this.upcomingShiftTotal =
            (pagedResult.metadata && pagedResult.metadata[0]?.total) || 0;
        } else {
          this.upcomingShifts = [];
          this.upcomingShiftTotal = 0;
        }
      });
  }

  loadCompletedShifts(): void {
    const payload = {
      pageIndex: this.completedShiftPageIndex,
      pageSize: this.shiftPageSize,
      sortField: "date",
      sortOrder: -1,
      startDate: this.filterStartDate,
      endDate: this.filterEndDate,
    };

    this.shiftService
      .getPagedShift(payload, this.nurseId)
      .subscribe((res: any) => {
        if (res && res.data && res.data.length) {
          const pagedResult = res.data[0];
          this.completedShifts = pagedResult.data;
          this.completedShiftTotal =
            (pagedResult.metadata && pagedResult.metadata[0]?.total) || 0;
        } else {
          this.completedShifts = [];
          this.completedShiftTotal = 0;
        }
      });
  }

  onFilter(): void {
    if (this.dateRange) {
      const startDate = this.dateRange.from.toLocalNativeDate();
      const endDate = this.dateRange.to.toLocalNativeDate();

      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);

      this.filterStartDate = startDate.toISOString();
      this.filterEndDate = endDate.toISOString();
      this.isFiltering = true;
    } else {
      this.filterStartDate = "";
      this.filterEndDate = "";
      this.isFiltering = false;
    }

    // Reset page indexes if needed
    this.upcomingShiftPageIndex = 1;
    this.completedShiftPageIndex = 1;
    this.loadUpcomingShifts();
    this.loadCompletedShifts();
  }

  resetFilter(): void {
    this.dateRange = null;
    this.filterStartDate = "";
    this.filterEndDate = "";
    this.isFiltering = false;
    this.upcomingShiftPageIndex = 1;
    this.completedShiftPageIndex = 1;
    this.loadUpcomingShifts();
    this.loadCompletedShifts();
  }

  loadSalaryRecords(): void {
    const payload = {
      pageIndex: this.salaryPageIndex,
      pageSize: this.salaryPageSize,
      sortField: "year",
      sortOrder: -1,
    };

    this.shiftService.getMonthlyShiftsByNurseIdPaginated(this.nurseId, payload).subscribe(
      (res: any) => {
        console.log("Paged salary records:", res.data);
        if (res.data && res.data[0]) {
          const pagedResult = res.data[0];
          this.salaryRecords = pagedResult.data;
          this.salaryTotal =
            (pagedResult.metadata && pagedResult.metadata[0]?.total) || 0;
        } else {
          this.salaryRecords = [];
          this.salaryTotal = 0;
        }
      },
      (error) => {
        console.error(error);
      }
    );
  }

  getMonthName(month: number): string {
    const date = new Date();
    date.setMonth(month - 1);
    return date.toLocaleString("default", { month: "long" });
  }

  navigateTo(index: number): void {
    switch (index) {
      case 0:
        this.router.navigate(["admin/nurse"]);
        break;
      case 1:
        this.router.navigate(["admin/nurse"]);
        break;
    }
  }

  private readonly alerts = inject(TuiAlertService);

  protected activeItemIndex = 0;

  leaveRecords: any[] = [];
  leaveYear = new Date().getFullYear();
  leaveMonth = new Date().getMonth() + 1;
  attendanceYear = new Date().getFullYear();
  attendanceMonth = new Date().getMonth() + 1;

  get leaveMonthLabel(): string {
    return new Date(this.leaveYear, this.leaveMonth - 1, 1).toLocaleString(
      "en",
      { month: "long", year: "numeric" }
    );
  }

  get attendanceMonthLabel(): string {
    return new Date(
      this.attendanceYear,
      this.attendanceMonth - 1,
      1
    ).toLocaleString("en", { month: "long", year: "numeric" });
  }

  get showConversionActions(): boolean {
    if (this.loggedInUserIsHR) {
      return false;
    }
    const s = this.nurse?.permanentConversionStatus;
    return (
      s === PERMANENT_CONVERSION_STATUS.ELIGIBLE ||
      s === PERMANENT_CONVERSION_STATUS.PENDING
    );
  }

  getEmployeeDisplayNumber(): string {
    return getEmployeeNumber(this.nurse);
  }

  protected confirmConversionDialogOpen = false;
  protected pendingConversionAction: "Approve" | "Pending" | "Reject" | null =
    null;

  get conversionDialogTitle(): string {
    return this.pendingConversionAction
      ? `${this.pendingConversionAction} permanent conversion`
      : "Confirm";
  }

  get conversionDialogMessage(): string {
    switch (this.pendingConversionAction) {
      case "Approve":
        return "Are you sure you want to approve this nurse for appointment as a permanent nurse?";
      case "Reject":
        return "Are you sure you want to reject this nurse's permanent conversion?";
      case "Pending":
        return "Are you sure you want to set this nurse's permanent conversion to pending?";
      default:
        return "Are you sure you want to proceed?";
    }
  }

  requestConversionAction(action: "Approve" | "Pending" | "Reject"): void {
    if (!this.nurseId) return;
    this.pendingConversionAction = action;
    this.confirmConversionDialogOpen = true;
  }

  onConversionActionCancel(): void {
    this.confirmConversionDialogOpen = false;
    this.pendingConversionAction = null;
  }

  onConversionActionConfirm(): void {
    if (!this.pendingConversionAction) return;
    const action = this.pendingConversionAction;
    this.confirmConversionDialogOpen = false;
    this.pendingConversionAction = null;
    this.onConversionAction(action);
  }

  onConversionAction(action: "Approve" | "Pending" | "Reject"): void {
    if (!this.nurseId) return;
    this.isLoading = true;
    this.nurseService.permanentConversion(this.nurseId, action).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        this.alerts
          .open(res?.message || `Conversion ${action} successful`, {
            label: "Success",
            appearance: "success",
          })
          .subscribe();
        if (res?.data?.redirectToEdit) {
          this.router.navigate(["/admin/nurse/add-edit", this.nurseId]);
        } else {
          this.loadNurseData();
        }
      },
      error: (err) => {
        this.isLoading = false;
        this.alerts
          .open(err?.error?.message || "Conversion action failed", {
            label: "Error",
            appearance: "error",
          })
          .subscribe();
      },
    });
  }

  loadLeaveRecords(): void {
    if (!this.nurseId) return;
    this.leaveService
      .getPagedLeaves({
        pageIndex: 0,
        pageSize: 100,
        filters: {
          nurseId: this.nurseId,
          year: this.leaveYear,
          month: this.leaveMonth,
        },
        sortField: "createdAt",
        sortOrder: -1,
      })
      .subscribe({
        next: (res: any) => {
          this.leaveRecords = res?.data || [];
        },
        error: () => {
          this.leaveRecords = [];
        },
      });
  }

  loadLeaveBalance(): void {
    if (!this.nurseId) return;
    this.leaveService.getBalance(this.nurseId).subscribe({
      next: (res: any) => (this.balance = res?.data),
      error: () => (this.balance = null)
    });
  }

  get availableCount(): number {
    if (!this.balance) return 0;
    const day = this.balance.dayOffs?.entitled ?? 0;
    const annual = this.balance.annualLeave?.eligible
      ? this.balance.annualLeave?.entitled ?? 0
      : 0;
    return day + annual;
  }

  get usedCount(): number {
    if (!this.balance) return 0;
    const day = this.balance.dayOffs?.used ?? 0;
    const annual = this.balance.annualLeave?.eligible
      ? this.balance.annualLeave?.used ?? 0
      : 0;
    return day + annual;
  }

  get remainingCount(): number {
    if (!this.balance) return 0;
    const day = this.balance.dayOffs?.remaining ?? 0;
    const annual = this.balance.annualLeave?.eligible
      ? this.balance.annualLeave?.remaining ?? 0
      : 0;
    return day + annual;
  }

  prevLeaveMonth(): void {
    if (this.leaveMonth === 1) {
      this.leaveMonth = 12;
      this.leaveYear -= 1;
    } else {
      this.leaveMonth -= 1;
    }
    this.loadLeaveRecords();
  }

  nextLeaveMonth(): void {
    if (this.leaveMonth === 12) {
      this.leaveMonth = 1;
      this.leaveYear += 1;
    } else {
      this.leaveMonth += 1;
    }
    this.loadLeaveRecords();
  }

  applyAttendanceMonthFilter(): void {
    const start = new Date(this.attendanceYear, this.attendanceMonth - 1, 1);
    const end = new Date(this.attendanceYear, this.attendanceMonth, 0);
    this.filterStartDate = start.toISOString();
    this.filterEndDate = end.toISOString();
    this.completedShiftPageIndex = 1;
    this.loadCompletedShifts();
  }

  prevAttendanceMonth(): void {
    if (this.attendanceMonth === 1) {
      this.attendanceMonth = 12;
      this.attendanceYear -= 1;
    } else {
      this.attendanceMonth -= 1;
    }
    this.applyAttendanceMonthFilter();
  }

  nextAttendanceMonth(): void {
    if (this.attendanceMonth === 12) {
      this.attendanceMonth = 1;
      this.attendanceYear += 1;
    } else {
      this.attendanceMonth += 1;
    }
    this.applyAttendanceMonthFilter();
  }

  protected onClick(item: string): void {
    this.alerts.open(item).subscribe();
  }

  resetUserPassword(email: any): void {
    if (!this.loggedInUserCanEdit) {
      this.alerts
        .open("You do not have permission to edit.", {
          label: "Error",
          appearance: "error",
        })
        .subscribe();
      return;
    }
    const payload = {
      email: email,
    };
    this.resetPasswordService.requestResetPasswordNurse(payload).subscribe({
      next: (response) => {
        this.alerts
          .open("Password reset information sent to nurse's email", {
            label: "Success",
            appearance: "success",
          })
          .subscribe();
      },
      error: (error) => {
        console.error("Error resetting password:", error);
      },
    });
  }

  toggleActive(): void {
    if (!this.loggedInUserCanEdit) {
      this.alerts
        .open("You do not have permission to edit.", {
          label: "Error",
          appearance: "error",
        })
        .subscribe();
      return;
    }
    this.isLoading = true;
    if (!this.nurse) return;

    const updatedNurse = { ...this.nurse, active: !this.nurse.active };

    this.nurseService.updateNurse(updatedNurse).subscribe({
      next: (response) => {
        this.nurse = response;
        const status = updatedNurse.active ? "actived" : "deactivated";
        this.alerts
          .open(`Nurse has been ${status} successfully.`, {
            label: "Success",
            appearance: "success",
          })
          .subscribe();
        this.loadNurseData();
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        console.error("Error updating nurse data:", error);
        this.alerts
          .open("Failed to update nurse data.", {
            label: "Error",
            appearance: "error",
          })
          .subscribe();
      },
    });
  }

  toggleArchive(): void {
    if (!this.loggedInUserCanEdit) {
      this.alerts
        .open("You do not have permission to edit.", {
          label: "Error",
          appearance: "error",
        })
        .subscribe();
      return;
    }
    this.isLoading = true;
    if (!this.nurse) return;

    const updatedNurse = { ...this.nurse, archived: !this.nurse.archived };

    this.nurseService.updateNurse(updatedNurse).subscribe({
      next: (response) => {
        this.nurse = response;
        const status = updatedNurse.archived ? "archived" : "unarchived";
        this.alerts
          .open(`Nurse has been ${status} successfully.`, {
            label: "Success",
            appearance: "success",
          })
          .subscribe();
        this.loadNurseData();
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        console.error("Error updating nurse data:", error);
        this.alerts
          .open("Failed to update nurse data.", {
            label: "Error",
            appearance: "error",
          })
          .subscribe();
      },
    });
  }

  toggleCancelShift(shift: IShift): void {
    this.isLoading = true;
    const shiftPayload = { status: "Canceled" };

    this.shiftService.updateShift(shift._id, shiftPayload).subscribe(
      (res: any) => {
        this.alerts
          .open("Shift has been updated successfully.", {
            label: "Success",
            appearance: "success",
          })
          .subscribe();
        this.loadUpcomingShifts();
        this.loadCompletedShifts();
        this.isLoading = false;
      },
      (error: any) => {
        this.alerts
          .open("Failed to update shift data.", {
            label: "Error",
            appearance: "error",
          })
          .subscribe();
        this.isLoading = false;
        console.error(error);
      }
    );
  }

  toggleAbsentShift(shift: IShift): void {
    this.isLoading = true;
    if (!this.nurse) {
      this.isLoading = false;
      return;
    }

    const shiftPayload = {
      status: "Absent",
      availabilityUpdated: true,
      salary: 0,
    };
    const nursePayload = { ...this.nurse, availability: true };

    const month = this.getMonth(shift.date);
    const year = this.getYear(shift.date);

    this.salaryService.getSalaryByMonth(month, year).subscribe(
      (res: any) => {
        const salaryRecords = res.data;
        const salaryRecord = salaryRecords.find((record: any) => record.nurseID === this.nurseId);

        if (!salaryRecord) {
          this.alerts
            .open("Salary record not found for the nurse.", {
              label: "Error",
              appearance: "error",
            })
            .subscribe();
          this.isLoading = false;
          return;
        }

        // Deduct the shift salary from the current salary.
        const updatedSalary = salaryRecord.salary - shift.salary;
        const salaryPayload = { salary: updatedSalary };

        // Execute the shift, nurse, and salary update concurrently.
        forkJoin([
          this.shiftService.updateShift(shift._id, shiftPayload),
          this.nurseService.updateNurse(nursePayload),
          this.salaryService.updateSalaryByNurseIdAndMonth(
            this.nurseId,
            month,
            year,
            salaryPayload
          ),
        ]).subscribe(
          ([shiftRes, nurseRes, salaryRes]) => {
            this.alerts
              .open("Shift, Nurse and Salary have been updated successfully.", {
                label: "Success",
                appearance: "success",
              })
              .subscribe();
            // Reload data after updates.
            this.loadUpcomingShifts();
            this.loadCompletedShifts();
            this.loadSalaryRecords();
            this.isLoading = false;
          },
          (error: any) => {
            console.error("Error updating data:", error);
            this.alerts
              .open("Failed to update data.", {
                label: "Error",
                appearance: "error",
              })
              .subscribe();
            this.isLoading = false;
          }
        );
      },
      (error: any) => {
        console.error("Error retrieving salary data:", error);
        this.alerts
          .open("Failed to retrieve salary data.", {
            label: "Error",
            appearance: "error",
          })
          .subscribe();
        this.isLoading = false;
      }
    );
  }

  getFormattedDate(date: Date | string): string {
    const dateObj = date instanceof Date ? date : new Date(date);

    if (isNaN(dateObj.getTime())) {
      return "Invalid Date";
    }

    const day = dateObj.getDate();
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const month = monthNames[dateObj.getMonth()];
    const year = dateObj.getFullYear();

    // Function to get ordinal suffix
    const getOrdinalSuffix = (day: number): string => {
      if (day > 3 && day < 21) return "th";
      switch (day % 10) {
        case 1:
          return "st";
        case 2:
          return "nd";
        case 3:
          return "rd";
        default:
          return "th";
      }
    };

    return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
  }

  toggleDropdown(shiftId: string): void {
    this.openMap[shiftId] = !this.openMap[shiftId];
  }

  getMonth(date: Date): number {
    return new Date(date).getMonth() + 1; // getMonth() is zero-indexed, so add 1
  }

  getYear(date: Date): number {
    return new Date(date).getFullYear();
  }

  viewSalaryDetails(nurseId: any, month: any, year: any): void {
    console.log("Salary record details:", month, year);
    this.router.navigate([`nurse/salary/${nurseId}/${month}/${year}`]);
  }

  onUpcomingShiftPageChange(page: number): void {
    this.upcomingShiftPageIndex = page;
    this.loadUpcomingShifts();
  }

  onCompletedShiftPageChange(page: number): void {
    this.completedShiftPageIndex = page;
    this.loadCompletedShifts();
  }

  getUpcomingShiftPageCount(): number {
    return Math.ceil(this.upcomingShiftTotal / this.shiftPageSize);
  }

  getCompletedShiftPageCount(): number {
    return Math.ceil(this.completedShiftTotal / this.shiftPageSize);
  }

  getSalaryPageCount(): number {
    return Math.ceil(this.salaryTotal / this.salaryPageSize);
  }

  onSalaryPageChange(page: number): void {
    this.salaryPageIndex = page;
    this.loadSalaryRecords();
  }

  getShiftStatusBgColor(status: string): string {
    switch (status) {
      case "Ongoing":
        return "#fd7e14";
      case "Upcoming":
        return "#007bff";
      case "Canceled":
        return "#dc3545";
      case "Completed":
        return "#6BC68A";
      case "Absent":
        return "#6c757d";
      default:
        return "gray";
    }
  }

  getServiceDurationColor(): string {
    if (!this.nurse || !this.nurse.registrationDate) {
      return 'gray';
    }
    const registrationDate = new Date(this.nurse.registrationDate);
    const now = new Date();

    let monthsDiff = (now.getFullYear() - registrationDate.getFullYear()) * 12;
    monthsDiff += now.getMonth() - registrationDate.getMonth();

    if (now.getDate() < registrationDate.getDate()) {
      monthsDiff--;
    }

    if (monthsDiff <= 3) {
      return '#87CEFA';
    } else if (monthsDiff <= 12) {
      return '#FFD700';
    } else {
      return '#90EE90';
    }
  }

  isServicePeriodLessThan6Months(): boolean {
    if (!this.nurse || !this.nurse.registrationDate) {
      return true;
    }
    const registrationDate = new Date(this.nurse.registrationDate);
    const now = new Date();

    let monthsDiff = (now.getFullYear() - registrationDate.getFullYear()) * 12;
    monthsDiff += now.getMonth() - registrationDate.getMonth();

    if (now.getDate() < registrationDate.getDate()) {
      monthsDiff--;
    }

    return monthsDiff < 6;
  }
  checkUserPermission(): void {
    this.loggedInUserCanEdit = this.authService.hasPermission("NURSE_UPDATE");
    this.loggedInUserIsHR = this.authService.getLoggedInUser()?.role === "HR";
  }

  getServicePeriodDisplay(registrationDate: string | Date | null | undefined): string {
    if (!registrationDate) return '—';
    const start = new Date(registrationDate);
    const end = new Date(); // today

    let years = end.getFullYear() - start.getFullYear();
    let months = end.getMonth() - start.getMonth();
    let days = end.getDate() - start.getDate();

    if (days < 0) {
      months--;
      const prevMonth = new Date(end.getFullYear(), end.getMonth(), 0);
      days += prevMonth.getDate();
    }

    if (months < 0) {
      years--;
      months += 12;
    }

    const totalMonths = years * 12 + months;

    if (totalMonths === 0 && days === 0) {
      return '0 days';
    }

    const monthPart = totalMonths > 0 ? `${totalMonths} month${totalMonths === 1 ? '' : 's'}` : '';
    const dayPart = days > 0 ? `${days} day${days === 1 ? '' : 's'}` : '';

    if (monthPart && dayPart) {
      return `${monthPart} and ${dayPart}`;
    }
    return monthPart || dayPart;
  }
}
